/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// js/custom-element/section/cart/shipping-estimator-table-model.js
const ZIPCODE_RANGES = [{
        province: "SP",
        from: 1e6,
        to: 19999999
    }, {
        province: "RJ",
        from: 2e7,
        to: 28999999
    }, {
        province: "ES",
        from: 29e6,
        to: 29999999
    }, {
        province: "MG",
        from: 3e7,
        to: 39999999
    }, {
        province: "BA",
        from: 4e7,
        to: 48999999
    }, {
        province: "SE",
        from: 49e6,
        to: 49999999
    }, {
        province: "PE",
        from: 5e7,
        to: 56999999
    }, {
        province: "AL",
        from: 57e6,
        to: 57999999
    }, {
        province: "PB",
        from: 58e6,
        to: 58999999
    }, {
        province: "RN",
        from: 59e6,
        to: 59999999
    }, {
        province: "CE",
        from: 6e7,
        to: 63999999
    }, {
        province: "PI",
        from: 64e6,
        to: 64999999
    }, {
        province: "MA",
        from: 65e6,
        to: 65999999
    }, {
        province: "PA",
        from: 66e6,
        to: 68899999
    }, {
        province: "AP",
        from: 689e5,
        to: 68999999
    }, {
        province: "AM",
        from: 69e6,
        to: 69299999
    }, {
        province: "RR",
        from: 693e5,
        to: 69399999
    }, {
        province: "AM",
        from: 694e5,
        to: 69899999
    }, {
        province: "AC",
        from: 699e5,
        to: 69999999
    }, {
        province: "DF",
        from: 7e7,
        to: 72799999
    }, {
        province: "GO",
        from: 728e5,
        to: 72999999
    }, {
        province: "DF",
        from: 73e6,
        to: 73699999
    }, {
        province: "GO",
        from: 737e5,
        to: 76799999
    }, {
        province: "RO",
        from: 768e5,
        to: 76999999
    }, {
        province: "TO",
        from: 77e6,
        to: 77999999
    }, {
        province: "MT",
        from: 78e6,
        to: 78899999
    }, {
        province: "MS",
        from: 79e6,
        to: 79999999
    }, {
        province: "PR",
        from: 8e7,
        to: 87999999
    }, {
        province: "SC",
        from: 88e6,
        to: 89999999
    }, {
        province: "RS",
        from: 9e7,
        to: 99999999
    }];

var ShippingEstimatorTableModel = class extends HTMLElement {
  connectedCallback() {
    this.submitButton = this.querySelector('[type="button"]');
    this.submitButton.addEventListener("click", this._estimateShipping.bind(this));
  }
  /**
   * @doc https://shopify.dev/docs/themes/ajax-api/reference/cart#generate-shipping-rates
   */
  async _estimateShipping() {
    const zip = this.querySelector('[name="shipping-estimator[zip]"]').value, country = "Brazil", province = this._provinceByZip(zip), isOutlet = this.dataset.outlet === 'true', location = await this._formatShippingLocationResults(zip);
    this.submitButton.setAttribute("aria-busy", "true");
    const prepareResponse = await fetch(`${window.themeVariables.routes.cartUrl}/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`, { method: "POST" });
    if (prepareResponse.ok) {
      const shippingRates = await this._getAsyncShippingRates(zip, country, province);
      this._formatShippingRates(shippingRates, isOutlet, location);
    } else {
      const jsonError = await prepareResponse.json();
      this._formatError(jsonError);
    }
    this.submitButton.removeAttribute("aria-busy");
  }
  async _getAsyncShippingRates(zip, country, province) {
    const response = await fetch(`${window.themeVariables.routes.cartUrl}/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`);
    const responseAsText = await response.text();
    if (responseAsText === "null") {
      return this._getAsyncShippingRates(zip, country, province);
    } else {
      return JSON.parse(responseAsText)["shipping_rates"];
    }
  }
  _formatShippingRates(shippingRates, isOutlet, location) {
    this.querySelector(".shipping-estimator__results")?.remove();
    let formattedShippingRates = "";
    shippingRates = shippingRates.filter(shippingRate => {
      const shippingType = shippingRate["presentment_name"].toLowerCase();
      return !(shippingType.includes('outlet') && !isOutlet);
    });
    shippingRates.forEach(shippingRate => {
      formattedShippingRates += `<tr>
                                  <td><strong style="color: #494949;">${shippingRate["presentment_name"]}</strong></td>
                                  <td>${shippingRate["description"] ? shippingRate["description"] : ""}</td>
                                  <td class="shipping-price"><strong>${formatMoney(parseFloat(shippingRate["price"]) * 100)}</strong></td>
                                </tr>`;
    });
    const html = `
      <div class="shipping-estimator__results">
        <p class="shipping-estimator__results-success">${location != "" ? location : shippingRates.length === 0 ? window.themeVariables.strings.shippingEstimatorNoResults : shippingRates.length === 1 ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
        ${formattedShippingRates === "" ? "" : 
          `<table class="fc_table">
            <thead>
              <tr class="fc_tt">
                <th>Entrega</th>
                <th>Prazo</th>
                <th>Preço</th>
              </tr>
            </thead>
            <tbody>
              ${formattedShippingRates}
            </tbody>
          </table>`}
      </div>
    `;
    this.insertAdjacentHTML("beforeend", html);
  }
  _provinceByZip(zip) {
    const zipCodeNumber = parseInt(zip.replace("-", ""))
    , zipCodeRange = ZIPCODE_RANGES.find((function(e) {
            return e.from <= zipCodeNumber && e.to >= zipCodeNumber;
        }
      ));
    return zipCodeRange ? zipCodeRange.province : "SP";
  }
  _formatError(errors) {
    this.querySelector(".shipping-estimator__results")?.remove();
    let formattedShippingRates = "";
    Object.keys(errors).forEach((errorKey) => {
      formattedShippingRates += `<li>${errors[errorKey]}</li>`;
    });
    const html = `
      <div class="shipping-estimator__results">
        <p class="shipping-estimator__results-error">${window.themeVariables.strings.shippingEstimatorError}</p>
        <ul class="shipping-estimator__results-error unordered-list">${formattedShippingRates}</ul>
      </div>
    `;
    this.insertAdjacentHTML("beforeend", html);
  }
  async _formatShippingLocationResults(zip) {
    const zipFormat = /^[0-9]{5}-[0-9]{3}$/;
    if (!zipFormat.test(zip)) return "";
    try {
      const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
      const data = await response.json();
      if (data.erro) return "";
      return `<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 28 28"><path fill="currentColor" d="M14 2.25A9.75 9.75 0 0 1 23.75 12c0 4.12-2.895 8.61-8.61 13.518a1.75 1.75 0 0 1-2.283-.002l-.378-.328C7.017 20.408 4.25 16.028 4.25 12A9.75 9.75 0 0 1 14 2.25m0 6a3.75 3.75 0 1 0 0 7.5a3.75 3.75 0 0 0 0-7.5"/></svg><span>${data.logradouro} - ${data.localidade} - ${data.uf}</span>`;
    } catch {
        return "";
    }
  }
};
window.customElements.define("shipping-estimator-table-model", ShippingEstimatorTableModel);